import { proxyCustomElement, HTMLElement, createEvent, h } from '@stencil/core/internal/client';
import { F as FormController } from './form.js';
import { O as Owner } from './Business.js';
import { i as identitySchema } from './business-identity-schema.js';
import { B as Button } from './button.js';
import { R as heading3 } from './parts.js';
import './details.js';
import { a as BusinessFormClickActions } from './event-types.js';
import { d as defineCustomElement$8 } from './custom-popper2.js';
import { d as defineCustomElement$7 } from './form-control-date2.js';
import { d as defineCustomElement$6 } from './form-control-number-masked2.js';
import { d as defineCustomElement$5 } from './form-control-select2.js';
import { d as defineCustomElement$4 } from './form-control-text2.js';
import { d as defineCustomElement$3 } from './form-control-tooltip2.js';
import { d as defineCustomElement$2 } from './identity-address-form.js';
import { d as defineCustomElement$1 } from './owner-form-inputs2.js';

const BusinessOwnerFormCore = /*@__PURE__*/ proxyCustomElement(class BusinessOwnerFormCore extends HTMLElement {
    loadingWatcher() {
        this.formLoading.emit(this.isLoading);
    }
    constructor() {
        super();
        this.__registerHost();
        this.clickEvent = createEvent(this, "click-event", 7);
        this.errorEvent = createEvent(this, "error-event", 7);
        this.formLoading = createEvent(this, "formLoading", 7);
        this.ownerSubmitted = createEvent(this, "ownerSubmitted", 7);
        this.isLoading = false;
        this.errors = {};
        this.owner = {};
        this.getData = () => {
            this.isLoading = true;
            this.getOwner({
                onSuccess: (response) => {
                    this.instantiateOwner(response.data);
                },
                onError: ({ error, code, severity }) => {
                    this.errorEvent.emit({
                        message: error,
                        errorCode: code,
                        severity: severity
                    });
                },
                final: () => this.isLoading = false
            });
        };
        this.sendData = () => {
            this.isLoading = true;
            return new Promise((resolve) => {
                if (this.ownerId) {
                    this.patchOwner({
                        payload: this.payload,
                        onSuccess: (response) => {
                            this.ownerSubmitted.emit({ id: response.data.id });
                            this.instantiateOwner(response.data);
                            resolve(true);
                        },
                        onError: ({ error, code, severity }) => {
                            this.errorEvent.emit({
                                message: error,
                                errorCode: code,
                                severity: severity
                            });
                            resolve(false);
                        },
                        final: () => this.isLoading = false
                    });
                }
                else {
                    this.postOwner({
                        payload: this.payload,
                        onSuccess: (response) => {
                            this.ownerSubmitted.emit({ id: response.data.id });
                            this.instantiateOwner(response.data);
                            resolve(true);
                        },
                        onError: ({ error, code, severity }) => {
                            this.errorEvent.emit({
                                message: error,
                                errorCode: code,
                                severity: severity
                            });
                            resolve(false);
                        },
                        final: () => this.isLoading = false
                    });
                }
            });
        };
        this.instantiateOwner = async (data) => {
            this.owner = Object.assign({}, new Owner(data));
            await this.formController.setInitialValues(this.owner);
        };
        this.handleAddOwner = () => {
            const eventName = this.ownerId ? BusinessFormClickActions.updateOwner : BusinessFormClickActions.addOwner;
            this.clickEvent.emit({ name: eventName });
        };
        this.handleRemoveOwner = () => {
            this.removeOwner(this.ownerId);
            this.clickEvent.emit({ name: BusinessFormClickActions.removeOwner });
        };
        this.validate = this.validate.bind(this);
        this.submit = this.submit.bind(this);
        this.validateAndSubmit = this.validateAndSubmit.bind(this);
    }
    async returnOwnerData() {
        return this.ownerId;
    }
    async validate() {
        return this.formController.validate();
    }
    async submit() {
        const isValid = await this.validate();
        if (isValid) {
            return this.sendData();
        }
    }
    async validateAndSubmit(event) {
        event.preventDefault();
        const isValid = await this.validate();
        if (isValid) {
            this.submit();
        }
    }
    get payload() {
        let formValues = new Owner(this.formController.values.getValue()).payload;
        if (this.ownerId) {
            return formValues;
        }
        else {
            return Object.assign(Object.assign({}, formValues), { business_id: this.businessId });
        }
    }
    get formTitle() {
        return this.ownerId ? 'Edit Business Owner' : 'Add Business Owner';
    }
    get submitButtonText() {
        return this.ownerId ? 'Update' : 'Add';
    }
    get showRemoveButton() {
        const blankForm = !this.ownerId && this.newFormOpen;
        if (this.ownersLength <= 1) {
            return false;
        }
        else {
            if (blankForm) {
                return true;
            }
            else if (this.ownerId && !this.newFormOpen) {
                return true;
            }
        }
    }
    componentWillLoad() {
        this.formController = new FormController(identitySchema('owner', this.allowOptionalFields));
        this.ownerId ? this.getData() : this.instantiateOwner({});
    }
    componentDidLoad() {
        this.formController.errors.subscribe(errors => (this.errors = Object.assign({}, errors)));
    }
    render() {
        return (h("form", { key: 'cf3b084a99c3cfc77af24f09feeda0468c2e6956', onSubmit: this.validateAndSubmit }, h("fieldset", { key: '3c78499a7df3496cd44416b96fe5fef44cc4d393' }, h("legend", { key: '5aee34b86e10d914964de3f62408b1f1e7899479', class: "fw-semibold fs-5", part: heading3 }, this.formTitle), h("br", { key: '4ca0d4c213f166f7ea10c0bfe276673ff077e1ba' }), h("div", { key: '95e485006a1111418ed409e7138c7f3098b9517c', class: "row gy-3" }, h("owner-form-inputs", { key: '1df9b40c91223253394c50312950b6b3d42906ff', ownerDefaultValue: this.formController.getInitialValues(), errors: this.errors, formController: this.formController }), h("div", { key: 'f1e2adc3111366c43a9a0d3cbed9e059916320f8', class: "d-flex gap-2 justify-content-start" }, h(Button, { key: 'ea19095c86d12cdabd48ad8618213aa06635b80f', variant: "secondary", type: "button", onClick: this.handleRemoveOwner, hidden: !this.showRemoveButton }, "Remove"), h(Button, { key: 'cb60a69f0e3252bb7623945610c5282bedfffe32', variant: "primary", type: "submit", onClick: this.handleAddOwner, disabled: this.isLoading }, this.submitButtonText))), h("hr", { key: 'db9fb6f130a114e82de0fb806ca1d70ac254f032' }))));
    }
    static get watchers() { return {
        "isLoading": ["loadingWatcher"]
    }; }
}, [0, "owner-form-core", {
        "ownerId": [1, "owner-id"],
        "businessId": [1, "business-id"],
        "getOwner": [16, "get-owner"],
        "patchOwner": [16, "patch-owner"],
        "postOwner": [16, "post-owner"],
        "allowOptionalFields": [4, "allow-optional-fields"],
        "removeOwner": [16, "remove-owner"],
        "newFormOpen": [4, "new-form-open"],
        "ownersLength": [2, "owners-length"],
        "isLoading": [32],
        "formController": [32],
        "errors": [32],
        "owner": [32],
        "returnOwnerData": [64],
        "validate": [64],
        "submit": [64],
        "validateAndSubmit": [64]
    }, undefined, {
        "isLoading": ["loadingWatcher"]
    }]);
function defineCustomElement() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["owner-form-core", "custom-popper", "form-control-date", "form-control-number-masked", "form-control-select", "form-control-text", "form-control-tooltip", "justifi-identity-address-form", "owner-form-inputs"];
    components.forEach(tagName => { switch (tagName) {
        case "owner-form-core":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, BusinessOwnerFormCore);
            }
            break;
        case "custom-popper":
            if (!customElements.get(tagName)) {
                defineCustomElement$8();
            }
            break;
        case "form-control-date":
            if (!customElements.get(tagName)) {
                defineCustomElement$7();
            }
            break;
        case "form-control-number-masked":
            if (!customElements.get(tagName)) {
                defineCustomElement$6();
            }
            break;
        case "form-control-select":
            if (!customElements.get(tagName)) {
                defineCustomElement$5();
            }
            break;
        case "form-control-text":
            if (!customElements.get(tagName)) {
                defineCustomElement$4();
            }
            break;
        case "form-control-tooltip":
            if (!customElements.get(tagName)) {
                defineCustomElement$3();
            }
            break;
        case "justifi-identity-address-form":
            if (!customElements.get(tagName)) {
                defineCustomElement$2();
            }
            break;
        case "owner-form-inputs":
            if (!customElements.get(tagName)) {
                defineCustomElement$1();
            }
            break;
    } });
}
defineCustomElement();

export { BusinessOwnerFormCore as B, defineCustomElement as d };
